import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import cloneDeep from 'lodash/cloneDeep';

import {
    CloseButton,
    Heading,
    ModalDialog,
    Spacer,
} from '@zillow/constellation';

import ClaimActions from 'app/shared/actions/ClaimActions';

import numberUtils from 'app/shared/utils/numberUtils';

import RemovableContactsList from './components/RemovableContactsList';
import DeleteClaimContact from './components/DeleteClaimContact';
import Footer from './components/Footer';

import { MANAGE_CLAIM_METHODS_STEPS } from './constants';

import { StyledBanner } from './StyledComponents';

const { formatPhoneNumberInput } = numberUtils;

const propTypes = {
    onClose: PropTypes.func.isRequired,
};

const { REMOVE_CLAIM_METHOD, VERIFY_CLAIM_CONTACT_TO_DELETE } = MANAGE_CLAIM_METHODS_STEPS;

const combineContactMethods = ({ emails, phones }) => [...emails, ...phones]
    .sort((a, b) => a.isVerified - b.isVerified);

const ManageClaimContactsMethodsModal = ({
    onClose,
    contactMethods,
}) => {
    const dispatch = useDispatch();
    const [currentStep, setCurrentStep] = useState(REMOVE_CLAIM_METHOD);
    const [contactToDelete, setContactToDelete] = useState({});
    const [banner, setBanner] = useState({ open: false, type: '' });
    const [contactDeleted, setContactDeleted] = useState(false);

    const incrementStep = () => {
        const newStep = currentStep === VERIFY_CLAIM_CONTACT_TO_DELETE
            ? REMOVE_CLAIM_METHOD
            : VERIFY_CLAIM_CONTACT_TO_DELETE;
        setCurrentStep(newStep);
    };

    const handleDeleteComplete = ({ success }) => {
        const { value, type } = contactToDelete;
        const { emails, phones } = contactMethods;
        const isEmail = type === 'email';
        const formattedDeletedContact = isEmail ? value : formatPhoneNumberInput(value);
        const emailsCopy = cloneDeep(emails);
        const phonesCopy = cloneDeep(phones);

        // if a listing is associated with the contact email or phone it should be deleted.
        const contactMethodToDeleteIndex = isEmail
            ? emails.findIndex(contactMethod => contactMethod.value === value)
            : phones.findIndex(contactMethod => contactMethod.value === value);

        if (isEmail) {
            emailsCopy.splice(contactMethodToDeleteIndex, 1);
            dispatch(ClaimActions.setClaimMethods({ emails: emailsCopy }));
        } else {
            phonesCopy.splice(contactMethodToDeleteIndex, 1);
            dispatch(ClaimActions.setClaimMethods({ phones: phonesCopy }));
        }

        setContactDeleted(true);
        incrementStep();
        setBanner({
            open: true,
            type: success ? 'success' : 'error',
            body: success ? `${formattedDeletedContact} successfully removed`
                : `There was an error removing ${formattedDeletedContact}. Try again.`,
        });
    };

    const handleCloseBanner = () => {
        setBanner({ open: false, type: '' });
        setContactToDelete({});
    };

    const headingText = currentStep === VERIFY_CLAIM_CONTACT_TO_DELETE ?
        'Confirm Contact Removal' : 'Manage Contact Methods';

    return (
        <ModalDialog
            isOpen={true}
            onClose={() => onClose(contactDeleted)}
            mediaQuery={{ xs: { fullScreen: true }, md: { fullScreen: false } }}
            wide={true}
            header={<Heading level={6}>{headingText}</Heading>}
            body={(
                <Spacer marginBottom="lg" className="ManageClaimContactsMethodsModal">
                    {banner.open && (
                        <StyledBanner
                            appearance={banner.type}
                            body={banner.body}
                            closeButton={<CloseButton />}
                            onClose={handleCloseBanner}
                        />
                    )}
                    <Spacer marginTop="xs" />
                    {currentStep === REMOVE_CLAIM_METHOD && (
                        <RemovableContactsList
                            contactMethods={combineContactMethods(contactMethods)}
                            incrementStep={incrementStep}
                            onContactMethodSelection={setContactToDelete}
                        />
                    )}
                    {currentStep === VERIFY_CLAIM_CONTACT_TO_DELETE && (
                        <DeleteClaimContact
                            contactToDelete={contactToDelete}
                        />
                    )}
                </Spacer>
            )}
            footer={(
                <Footer
                    contactToDelete={contactToDelete}
                    currentStep={currentStep}
                    incrementStep={incrementStep}
                    onDeleteComplete={handleDeleteComplete}
                    onClose={() => onClose(contactDeleted)}
                />
            )}
        />
    );
};

ManageClaimContactsMethodsModal.propTypes = propTypes;

export default ManageClaimContactsMethodsModal;
